/**
 * One forge: detail, test builds, the publish submission, and every
 * non-turn mutation (start/stop, model tier, test build, publish).
 *
 * Polling is deliberate and bounded: the detail refetches every 5 s while
 * the platform is moving the row without us (transitional status, a queued
 * control command, an unresolved publish), and every 30 s while it runs — the
 * platform stops a running forge on its own (30 minutes idle, the month's
 * hours used up; ADR-023 A21), and a page that never asked again kept showing
 * it as running. The test-builds list while a build is in flight; the
 * submission while it is not terminal. Everything else is invalidation-driven.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { type ForgeDetailResult, type PluginSubmissionDetailResult } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { forgeQueryKeys } from '../api/queryKeys';
import {
  FORGE_TRANSITIONAL_STATUSES,
  type ForgeModelTier,
  type ForgeSize,
  type ForgeSummary,
  type ForgeTestBuildSummary,
} from '../types';

const POLL_MS = 5_000;
/** A running forge can be stopped by the platform; this is how soon the page notices. */
const RUNNING_POLL_MS = 30_000;
/** How long the detail keeps polling for a submission id after "Deploy to production". */
const PUBLISH_FOLLOW_MS = 10 * 60_000;
/** Submission statuses after which the publish outcome no longer changes. */
const TERMINAL_SUBMISSION_STATUSES: ReadonlySet<string> = new Set([
  'published',
  'rejected',
  'failed',
  'build_failed',
  'review_failed',
  'cancelled',
  'canceled',
]);

export interface UseForgeResult {
  forge: ForgeSummary | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  testBuilds: ForgeTestBuildSummary[];
  /** The latest READY build, if any — what "Open test version" renders. */
  readyTestBuild: ForgeTestBuildSummary | null;
  submission: PluginSubmissionDetailResult | null;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  setModelTier: (tier: ForgeModelTier | null) => Promise<void>;
  /** Only while stopped/failed; `large` is Pro-only. Failures surface via `error`. */
  setSize: (size: ForgeSize) => Promise<void>;
  /** Rejects with the server's message, so the editor can show it. */
  rename: (name: string) => Promise<void>;
  requestTestBuild: () => Promise<void>;
  publish: (options?: { carryTestData: boolean }) => Promise<void>;
  /** Clear: the next message starts a new conversation (ADR-023 A21). Failures surface via `error`. */
  startNewConversation: () => Promise<void>;
  /** Undo a Clear before the next message is sent. */
  cancelNewConversation: () => Promise<void>;
  isMutating: boolean;
  isBuildRequested: boolean;
  isPublishRequested: boolean;
}

function errorMessage(err: unknown): string | null {
  if (!err) return null;
  return err instanceof Error ? err.message : String(err);
}

export function useForge(forgeId: string | null): UseForgeResult {
  const queryClient = useQueryClient();
  const [mutationError, setMutationError] = useState<string | null>(null);
  // After "Deploy to production" the submission id appears on the detail
  // only once the runtime has packaged + submitted — AFTER `pendingCommand`
  // clears (it clears on delivery). Remember the id we had at click time and
  // keep polling until it changes, for at most PUBLISH_FOLLOW_MS.
  const publishFollowRef = useRef<{ baseline: string | null; until: number } | null>(null);

  const detailQuery = useQuery({
    queryKey: forgeQueryKeys.detail(forgeId ?? ''),
    queryFn: () => api.getForge(forgeId!),
    enabled: !!forgeId,
    refetchInterval: (query) => {
      const data = query.state.data;
      const forge = data?.forge;
      if (!forge) return false;
      const follow = publishFollowRef.current;
      if (follow) {
        const current = data.lastSubmissionId ?? null;
        if ((current !== null && current !== follow.baseline) || Date.now() > follow.until) {
          publishFollowRef.current = null;
        } else {
          return POLL_MS;
        }
      }
      const moving = FORGE_TRANSITIONAL_STATUSES.has(forge.status) || forge.pendingCommand !== null;
      if (moving) return POLL_MS;
      return forge.status === 'running' ? RUNNING_POLL_MS : false;
    },
  });
  const forge = detailQuery.data?.forge ?? null;

  const buildsQuery = useQuery({
    queryKey: forgeQueryKeys.testBuilds(forgeId ?? ''),
    queryFn: () => api.listForgeTestBuilds(forgeId!),
    enabled: !!forgeId,
    refetchInterval: (query) => {
      const builds = query.state.data?.builds ?? [];
      const building = builds.some((b) => b.status === 'building');
      return building || forge?.pendingCommand === 'test_build' ? POLL_MS : false;
    },
  });
  const testBuilds = useMemo(() => buildsQuery.data?.builds ?? [], [buildsQuery.data]);
  const readyTestBuild = useMemo(
    () => testBuilds.find((b) => b.status === 'ready') ?? null,
    [testBuilds],
  );

  // The production publish, once the runtime has submitted (`lastSubmissionId`
  // rides on the detail response next to the forge; tolerated absent).
  const submissionId = detailQuery.data?.lastSubmissionId ?? null;
  const submissionQuery = useQuery({
    queryKey: queryKeys.submissions.detail(submissionId ?? ''),
    queryFn: () => api.getPluginSubmission(submissionId!),
    enabled: !!submissionId,
    refetchInterval: (query) => {
      const status = query.state.data?.submission.status;
      return status && TERMINAL_SUBMISSION_STATUSES.has(status) ? false : POLL_MS;
    },
  });

  const setForgeInCache = useCallback(
    (updated: ForgeSummary) => {
      // Keep the sibling fields (lastSubmissionId) the detail already holds.
      queryClient.setQueryData<ForgeDetailResult>(forgeQueryKeys.detail(updated.id), (prev) => ({
        ...(prev ?? {}),
        forge: updated,
      }));
      void queryClient.invalidateQueries({ queryKey: forgeQueryKeys.lists() });
    },
    [queryClient],
  );

  const lifecycleMutation = useMutation({
    mutationFn: ({ action }: { action: 'start' | 'stop' }) =>
      action === 'start' ? api.startForge(forgeId!) : api.stopForge(forgeId!),
    onSuccess: (result) => {
      setMutationError(null);
      setForgeInCache(result.forge);
    },
    onError: (err) => setMutationError(errorMessage(err)),
  });

  const tierMutation = useMutation({
    mutationFn: (modelOptIn: ForgeModelTier | null) => api.updateForge(forgeId!, { modelOptIn }),
    onSuccess: (result) => {
      setMutationError(null);
      setForgeInCache(result.forge);
    },
    onError: (err) => setMutationError(errorMessage(err)),
  });

  const sizeMutation = useMutation({
    mutationFn: (size: ForgeSize) => api.updateForge(forgeId!, { size }),
    onSuccess: (result) => {
      setMutationError(null);
      setForgeInCache(result.forge);
    },
    onError: (err) => setMutationError(errorMessage(err)),
  });

  const renameMutation = useMutation({
    mutationFn: (name: string) => api.updateForge(forgeId!, { name }),
    onSuccess: (result) => setForgeInCache(result.forge),
  });

  const testBuildMutation = useMutation({
    mutationFn: () => api.requestForgeTestBuild(forgeId!),
    onSuccess: (result) => {
      setMutationError(null);
      setForgeInCache(result.forge);
      void queryClient.invalidateQueries({ queryKey: forgeQueryKeys.testBuilds(forgeId!) });
    },
    onError: (err) => setMutationError(errorMessage(err)),
  });

  const publishMutation = useMutation({
    mutationFn: (options: { carryTestData: boolean }) => api.publishForge(forgeId!, options),
    onSuccess: (result) => {
      setMutationError(null);
      publishFollowRef.current = {
        baseline: detailQuery.data?.lastSubmissionId ?? null,
        until: Date.now() + PUBLISH_FOLLOW_MS,
      };
      // Writing the row re-evaluates `refetchInterval`, which now sees the follow.
      setForgeInCache(result.forge);
    },
    onError: (err) => setMutationError(errorMessage(err)),
  });

  const conversationMutation = useMutation({
    mutationFn: (action: 'new' | 'cancel') =>
      action === 'new'
        ? api.startNewForgeConversation(forgeId!)
        : api.cancelNewForgeConversation(forgeId!),
    onSuccess: (result) => {
      setMutationError(null);
      setForgeInCache(result.forge);
    },
    onError: (err) => setMutationError(errorMessage(err)),
  });

  const start = useCallback(async () => {
    await lifecycleMutation.mutateAsync({ action: 'start' });
  }, [lifecycleMutation]);
  const stop = useCallback(async () => {
    await lifecycleMutation.mutateAsync({ action: 'stop' });
  }, [lifecycleMutation]);
  const setModelTier = useCallback(
    async (tier: ForgeModelTier | null) => {
      await tierMutation.mutateAsync(tier);
    },
    [tierMutation],
  );
  const rename = useCallback(
    async (name: string) => {
      await renameMutation.mutateAsync(name);
    },
    [renameMutation],
  );

  const setSize = useCallback(
    async (size: ForgeSize) => {
      await sizeMutation.mutateAsync(size);
    },
    [sizeMutation],
  );
  const requestTestBuild = useCallback(async () => {
    await testBuildMutation.mutateAsync();
  }, [testBuildMutation]);
  const publish = useCallback(
    async (options: { carryTestData: boolean } = { carryTestData: false }) => {
      await publishMutation.mutateAsync(options);
    },
    [publishMutation],
  );
  const startNewConversation = useCallback(async () => {
    await conversationMutation.mutateAsync('new');
  }, [conversationMutation]);
  const cancelNewConversation = useCallback(async () => {
    await conversationMutation.mutateAsync('cancel');
  }, [conversationMutation]);

  return {
    forge,
    isLoading: detailQuery.isLoading,
    error: mutationError ?? errorMessage(detailQuery.error),
    refresh: () => {
      void detailQuery.refetch();
      void buildsQuery.refetch();
    },
    testBuilds,
    readyTestBuild,
    submission: submissionQuery.data ?? null,
    start,
    stop,
    setModelTier,
    setSize,
    rename,
    requestTestBuild,
    publish,
    startNewConversation,
    cancelNewConversation,
    isMutating:
      lifecycleMutation.isPending ||
      conversationMutation.isPending ||
      tierMutation.isPending ||
      sizeMutation.isPending ||
      testBuildMutation.isPending ||
      publishMutation.isPending,
    isBuildRequested: testBuildMutation.isPending || forge?.pendingCommand === 'test_build',
    isPublishRequested: publishMutation.isPending || forge?.pendingCommand === 'publish',
  };
}
